import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PiXLight,
  PiQuestionLight,
  PiCaretDownLight,
  PiCaretRightLight,
  PiBriefcaseLight,
  PiCheckSquareOffsetLight,
  PiUsersLight,
  PiMagnifyingGlassLight,
  PiFileTextLight,
  PiArticleLight,
  PiDatabaseLight,
  PiTableLight,
  PiChatsCircleLight,
  PiChartBarLight,
  PiLightningLight,
  PiShieldCheckLight,
  PiTrendUpLight,
  PiPaperPlaneRightLight,
  PiChatDotsLight,
  PiBuildingsLight,
  PiUserLight,
} from "react-icons/pi";
import apis from "../../utils/apis";
import Button from "../shared/TailwindComponents/Button";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AnalyticsHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  isOrgAdmin: boolean;
}

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  metrics: HelpMetric[];
}

interface HelpMetric {
  name: string;
  explanation: string;
  howCalculated: string;
  whyItMatters: string;
  type: "card" | "chart" | "gauge";
}

/* ------------------------------------------------------------------ */
/*  Help Content                                                       */
/* ------------------------------------------------------------------ */

const PERSONAL_SECTIONS: HelpSection[] = [
  {
    id: "matters",
    title: "Matters",
    icon: <PiBriefcaseLight className="w-4 h-4" />,
    description: "Everything related to your legal matters - cases, disputes, transactions, and other work items you manage.",
    metrics: [
      {
        name: "Total matters",
        explanation: "The number of matters (cases) you created in the selected time period.",
        howCalculated: "Counts all matters you opened between the start and end dates.",
        whyItMatters: "Helps you understand your workload volume over time.",
        type: "card",
      },
      {
        name: "Matters over time",
        explanation: "A timeline showing how many new matters you opened each day, week, or month.",
        howCalculated: "Groups your created matters into time buckets (days, weeks, or months) so you can spot trends.",
        whyItMatters: "Reveals busy periods and helps with planning ahead.",
        type: "chart",
      },
      {
        name: "Status breakdown",
        explanation: "A pie chart showing how your matters are distributed across stages like Open, In Progress, Closed, etc.",
        howCalculated: "Groups every matter by its current status and shows the proportion of each.",
        whyItMatters: "Quickly see how much of your work is still pending versus completed.",
        type: "chart",
      },
      {
        name: "Category breakdown",
        explanation: "Shows the types of matters you work on, such as Corporate, Litigation, Real Estate, etc.",
        howCalculated: "Groups matters by their assigned category (practice area) and counts each.",
        whyItMatters: "Understand your specialization and workload by area.",
        type: "chart",
      },
      {
        name: "Jurisdiction",
        explanation: "Where your matters are filed - grouped by state, country, or region.",
        howCalculated: "Extracts the jurisdiction from each matter and counts how many fall in each location.",
        whyItMatters: "Shows your geographic coverage and filing patterns.",
        type: "chart",
      },
      {
        name: "Overdue matters",
        explanation: "The number of matters past their due date that are still open.",
        howCalculated: "Counts open matters where the deadline has passed and the matter isn't closed yet.",
        whyItMatters: "Highlights work that needs urgent attention.",
        type: "card",
      },
      {
        name: "Confidential matters",
        explanation: "How many of your matters are marked as confidential.",
        howCalculated: "Counts matters with the confidential flag turned on.",
        whyItMatters: "Keeps you aware of sensitive work in your portfolio.",
        type: "card",
      },
      {
        name: "AI-processed matters",
        explanation: "Matters that have been analyzed or enriched by the AI engine.",
        howCalculated: "Counts matters where the AI processing step has been completed.",
        whyItMatters: "Shows how much of your work benefits from AI assistance.",
        type: "card",
      },
      {
        name: "Average matter duration",
        explanation: "The typical time it takes you to close a matter, from creation to completion.",
        howCalculated: "Measures the number of days between when a matter was created and when it was closed, then averages across all closed matters.",
        whyItMatters: "Helps you benchmark your efficiency and set realistic timelines.",
        type: "card",
      },
    ],
  },
  {
    id: "tasks",
    title: "Tasks",
    icon: <PiCheckSquareOffsetLight className="w-4 h-4" />,
    description: "Individual to-do items and action steps within your matters.",
    metrics: [
      {
        name: "Total tasks",
        explanation: "The number of tasks assigned to you in the selected time period.",
        howCalculated: "Counts all tasks created and assigned to you between the selected dates.",
        whyItMatters: "Tracks your day-to-day actionable workload.",
        type: "card",
      },
      {
        name: "Tasks over time",
        explanation: "A timeline showing when tasks were created for you.",
        howCalculated: "Groups tasks by their creation date into time buckets.",
        whyItMatters: "Reveals when you're being assigned the most work.",
        type: "chart",
      },
      {
        name: "Overdue tasks",
        explanation: "The number of tasks past their due date that are still not completed.",
        howCalculated: "Counts tasks where the deadline has passed but no completion date is recorded.",
        whyItMatters: "Flags items that need immediate attention.",
        type: "card",
      },
      {
        name: "Average turnaround time",
        explanation: "How long it typically takes you to complete a task after it's assigned.",
        howCalculated: "Measures the days between task creation and completion, then averages across all completed tasks.",
        whyItMatters: "Measures your responsiveness and helps set better deadlines.",
        type: "card",
      },
    ],
  },
  {
    id: "clients",
    title: "Clients",
    icon: <PiUsersLight className="w-4 h-4" />,
    description: "Your client relationships - who you work with and how your client base grows.",
    metrics: [
      {
        name: "Total clients",
        explanation: "The number of clients you manage or created in the selected time period.",
        howCalculated: "Counts all clients linked to your account that were created within the date range.",
        whyItMatters: "Shows the size and growth of your client portfolio.",
        type: "card",
      },
      {
        name: "Clients over time",
        explanation: "A timeline showing when you added new clients.",
        howCalculated: "Groups your new clients by the date they were created.",
        whyItMatters: "Visualizes how your client base grows over time.",
        type: "chart",
      },
      {
        name: "Average matters per client",
        explanation: "How many matters/cases you handle per client on average.",
        howCalculated: "Divides the total number of your matters by the total number of your clients.",
        whyItMatters: "Indicates the depth of your client relationships.",
        type: "card",
      },
    ],
  },
  {
    id: "research",
    title: "Research",
    icon: <PiMagnifyingGlassLight className="w-4 h-4" />,
    description: "Your AI-powered legal research activity - queries, tokens used, and citation results.",
    metrics: [
      {
        name: "Research queries",
        explanation: "The number of research questions you asked the AI research engine.",
        howCalculated: "Counts every research query you submitted during the selected period.",
        whyItMatters: "Tracks how actively you use the AI research tool.",
        type: "card",
      },
      {
        name: "Tokens used",
        explanation: "A measure of how much AI processing power your research consumed.",
        howCalculated: "Sums up the total tokens (units of text processed) across all your research queries.",
        whyItMatters: "Helps you understand your AI resource usage.",
        type: "card",
      },
      {
        name: "Citations found",
        explanation: "The total number of relevant legal citations the AI found for your queries.",
        howCalculated: "Sums the citations returned across all your research results.",
        whyItMatters: "Shows the quality and depth of your research output.",
        type: "card",
      },
      {
        name: "Research over time",
        explanation: "A timeline of your research activity.",
        howCalculated: "Groups your research queries by date to show usage patterns.",
        whyItMatters: "Reveals your research habits and peak activity periods.",
        type: "chart",
      },
      {
        name: "Average research response time",
        explanation: "How quickly the AI returns results for your queries.",
        howCalculated: "Measures the seconds between when you submit a query and when results arrive, averaged across all queries.",
        whyItMatters: "Helps set expectations for research turnaround.",
        type: "card",
      },
    ],
  },
  {
    id: "drafts",
    title: "Drafts and insights",
    icon: <PiFileTextLight className="w-4 h-4" />,
    description: "Documents drafted and AI-generated insights created.",
    metrics: [
      {
        name: "Drafts created",
        explanation: "The number of document drafts you created.",
        howCalculated: "Counts all drafts linked to your account during the period.",
        whyItMatters: "Tracks your document production.",
        type: "card",
      },
      {
        name: "Insights generated",
        explanation: "AI-generated summaries and recommendations produced from your data.",
        howCalculated: "Counts insight entries generated from your workflows.",
        whyItMatters: "Shows how much AI-assisted analysis you're leveraging.",
        type: "card",
      },
    ],
  },
  {
    id: "files",
    title: "Files",
    icon: <PiFileTextLight className="w-4 h-4" />,
    description: "Documents, images, and other files you've uploaded to the platform.",
    metrics: [
      {
        name: "Files uploaded",
        explanation: "The total number of files you uploaded during the selected period.",
        howCalculated: "Counts every file you uploaded between the start and end dates.",
        whyItMatters: "Measures how actively you use the document management system.",
        type: "card",
      },
      {
        name: "Storage used",
        explanation: "The total disk space your uploaded files occupy.",
        howCalculated: "Sums the file sizes of all your uploads and displays it in a readable format (KB, MB, GB).",
        whyItMatters: "Helps you manage your storage quota.",
        type: "card",
      },
      {
        name: "Files over time",
        explanation: "A timeline of your file uploads.",
        howCalculated: "Groups uploads by date to show your uploading activity.",
        whyItMatters: "Reveals your document management patterns.",
        type: "chart",
      },
    ],
  },
  {
    id: "templates",
    title: "Templates",
    icon: <PiArticleLight className="w-4 h-4" />,
    description: "Reusable document templates you've created or managed.",
    metrics: [
      {
        name: "Total templates",
        explanation: "The number of templates you own.",
        howCalculated: "Counts all active templates linked to your account.",
        whyItMatters: "Shows how much reusable content you've built.",
        type: "card",
      },
      {
        name: "Templates over time",
        explanation: "When you created your templates.",
        howCalculated: "Groups template creation dates into a timeline.",
        whyItMatters: "Tracks your investment in workflow efficiency.",
        type: "chart",
      },
    ],
  },
  {
    id: "data-sources",
    title: "Data sources",
    icon: <PiDatabaseLight className="w-4 h-4" />,
    description: "Knowledge bases and indexed document libraries you use for research.",
    metrics: [
      {
        name: "Knowledge bases",
        explanation: "The number of data source collections you've set up.",
        howCalculated: "Counts all data source entries you created.",
        whyItMatters: "Shows the breadth of your knowledge resources.",
        type: "card",
      },
      {
        name: "Indexed documents",
        explanation: "How many individual documents have been processed and indexed.",
        howCalculated: "Sums the indexed document count across all your data sources.",
        whyItMatters: "Indicates the depth of searchable content available to you.",
        type: "card",
      },
    ],
  },
  {
    id: "tables",
    title: "Tables",
    icon: <PiTableLight className="w-4 h-4" />,
    description: "Structured data tables you've created for analysis.",
    metrics: [
      {
        name: "Total tables",
        explanation: "The number of data tables you've created.",
        howCalculated: "Counts all active tables linked to your account.",
        whyItMatters: "Tracks your structured data management activity.",
        type: "card",
      },
      {
        name: "Documents analyzed",
        explanation: "How many documents have been processed through your tables.",
        howCalculated: "Sums the analyzed document count across all your tables.",
        whyItMatters: "Shows how much structured analysis you've performed.",
        type: "card",
      },
    ],
  },
  {
    id: "personal-productivity",
    title: "Your productivity",
    icon: <PiTrendUpLight className="w-4 h-4" />,
    description: "Personal performance metrics - track your efficiency, AI usage, workload balance, and research quality.",
    metrics: [
      {
        name: "Task on-time rate",
        explanation: "A gauge showing what percentage of your tasks you completed before their deadline.",
        howCalculated: "Of all your completed tasks that had a due date, measures how many were finished on or before that date.",
        whyItMatters: "Helps you understand your reliability and time management. A high percentage shows strong deadline discipline.",
        type: "gauge",
      },
      {
        name: "Your AI usage",
        explanation: "A gauge showing how much you leverage AI tools - both for processing matters and conducting research.",
        howCalculated: "Combines two measures: the percentage of your matters that were AI-processed and a score based on your research query volume, then averages them.",
        whyItMatters: "Shows whether you're taking full advantage of the AI tools available to you.",
        type: "gauge",
      },
      {
        name: "Your productivity trend",
        explanation: "A line chart showing how many tasks you completed over time.",
        howCalculated: "Groups your completed tasks by date into time buckets (days, weeks, or months).",
        whyItMatters: "Reveals whether your output is increasing, steady, or declining - and helps you spot your most productive periods.",
        type: "chart",
      },
      {
        name: "Your research quality",
        explanation: "A line chart tracking the percentage of your research results that received positive feedback.",
        howCalculated: "Looks at feedback you gave on research results ('thumbs up' vs others) and plots the positive rate over time.",
        whyItMatters: "A rising trend means the research tool is getting you better results. A declining trend may suggest trying different search strategies.",
        type: "chart",
      },
      {
        name: "Your matter stages",
        explanation: "A bar chart showing how your matters are distributed across pipeline stages.",
        howCalculated: "Counts your matters in each stage (Intake, Review, Active, Closed, etc.) and displays them in order.",
        whyItMatters: "Quickly see where your cases are piling up and what needs to move forward.",
        type: "chart",
      },
      {
        name: "Your workload snapshot",
        explanation: "A bar chart showing your current open matters, pending tasks, and overdue tasks at a glance.",
        howCalculated: "Counts open (not closed) matters, incomplete tasks, and tasks past their due date.",
        whyItMatters: "A quick health check on your current workload. If overdue tasks are rising, it's time to reprioritize.",
        type: "chart",
      },
    ],
  },
];

const EXECUTIVE_SECTIONS: HelpSection[] = [
  {
    id: "matter-pipeline",
    title: "Matter pipeline and velocity",
    icon: <PiChartBarLight className="w-4 h-4" />,
    description: "Understand how matters flow through your organization's pipeline - from intake to closure.",
    metrics: [
      {
        name: "Matter pipeline funnel",
        explanation: "A bar chart showing how many matters sit at each stage of your workflow (e.g., Intake, Review, Active, Closed).",
        howCalculated: "Counts all active matters in each stage, ordered by your workflow sequence. This gives you a snapshot of where work is piling up.",
        whyItMatters: "Instantly spot bottlenecks. If too many matters are stuck in one stage, it signals a process issue that needs attention.",
        type: "chart",
      },
      {
        name: "Matter stage velocity",
        explanation: "Shows the average number of days matters spend in each stage before moving to the next.",
        howCalculated: "Looks at the history of stage changes for each matter and measures the time gap between entering and leaving each stage, then averages across all matters.",
        whyItMatters: "Identifies slow stages in your pipeline. If matters spend weeks in 'Review' but only days in other stages, that's where to focus improvement efforts.",
        type: "chart",
      },
      {
        name: "Priority vs. type matrix",
        explanation: "A grouped bar chart crossing matter priority levels (High, Medium, Low) with matter types (Litigation, Corporate, etc.).",
        howCalculated: "Groups all matters by their priority level and category, then counts each combination.",
        whyItMatters: "Reveals whether high-priority work is concentrated in a specific practice area, helping you allocate resources more effectively.",
        type: "chart",
      },
    ],
  },
  {
    id: "team-workload",
    title: "Team and workload",
    icon: <PiUsersLight className="w-4 h-4" />,
    description: "How work is distributed across your team - balance, productivity, and collaboration patterns.",
    metrics: [
      {
        name: "Lawyer workload balance",
        explanation: "A bar chart showing how many matters and tasks each team member is handling.",
        howCalculated: "For each active team member, counts their assigned matters and tasks, then displays them side by side.",
        whyItMatters: "Prevents burnout by showing if work is evenly distributed or if some people are overloaded while others have capacity.",
        type: "chart",
      },
      {
        name: "Team productivity score",
        explanation: "A radar chart comparing each team member's task completion performance.",
        howCalculated: "Counts the number of tasks each active team member completed within the selected period and plots them on a radar chart.",
        whyItMatters: "Gives a visual comparison of individual productivity, helping identify top performers and those who may need support.",
        type: "chart",
      },
      {
        name: "Department comparison",
        explanation: "A bar chart comparing departments by team size.",
        howCalculated: "Counts how many active members each department has.",
        whyItMatters: "Provides a quick overview of organizational structure and resource distribution across departments.",
        type: "chart",
      },
      {
        name: "Collaboration intensity",
        explanation: "A line chart tracking message volume in your team chat over time.",
        howCalculated: "Counts chat messages sent each day/week/month across all organization channels.",
        whyItMatters: "High collaboration often correlates with better outcomes. Tracking this helps you understand team communication health.",
        type: "chart",
      },
    ],
  },
  {
    id: "clients-executive",
    title: "Client portfolio",
    icon: <PiUsersLight className="w-4 h-4" />,
    description: "Track how your client base grows and how well your team serves them.",
    metrics: [
      {
        name: "Client portfolio growth",
        explanation: "A line chart showing new client additions and your cumulative client count over time.",
        howCalculated: "Plots two lines: (1) how many new clients were added each period, and (2) the running total of all clients up to that point.",
        whyItMatters: "Shows whether your client base is growing, plateauing, or declining - a key indicator of business health.",
        type: "chart",
      },
      {
        name: "Client onboarding funnel",
        explanation: "A bar chart showing Total Clients, then Onboarded, then Active, like a conversion funnel.",
        howCalculated: "Counts how many clients exist, how many have completed onboarding, and how many are actively engaged.",
        whyItMatters: "If many clients are created but few become active, your onboarding process may need improvement.",
        type: "chart",
      },
      {
        name: "Clients per lawyer",
        explanation: "The average number of active clients per active team member.",
        howCalculated: "Divides total active clients by total active team members.",
        whyItMatters: "A high ratio may indicate stretched capacity; a low ratio might suggest room for growth.",
        type: "card",
      },
      {
        name: "Top clients by activity",
        explanation: "A bar chart of your most active clients ranked by the total number of matters and tasks they're involved in.",
        howCalculated: "Sums each client's matters and tasks, then ranks the top 10.",
        whyItMatters: "Highlights your most engaged clients - great for relationship management and prioritization.",
        type: "chart",
      },
    ],
  },
  {
    id: "ai-research",
    title: "AI and research intelligence",
    icon: <PiLightningLight className="w-4 h-4" />,
    description: "Measure how effectively your organization leverages AI tools and research capabilities.",
    metrics: [
      {
        name: "AI utilization rate",
        explanation: "A gauge showing what percentage of your organization's work involves AI assistance.",
        howCalculated: "Combines two measures: (1) the percentage of matters processed by AI, and (2) the percentage of team members who use the research tool. The gauge shows the average of both.",
        whyItMatters: "Low utilization suggests the team may not be taking full advantage of AI tools - an opportunity for training or process improvement.",
        type: "gauge",
      },
      {
        name: "Tokens per citation",
        explanation: "The average amount of AI processing needed to find one useful citation.",
        howCalculated: "Divides total tokens consumed by total citations found across all research queries.",
        whyItMatters: "Lower is better - fewer tokens per citation means more efficient research. A rising number could indicate the need to refine search strategies.",
        type: "card",
      },
      {
        name: "Research quality trend",
        explanation: "A line chart showing the percentage of research results that received positive feedback over time.",
        howCalculated: "Tracks the ratio of 'thumbs up' feedback to total feedback for research results, plotted over time.",
        whyItMatters: "Rising quality means the AI is delivering more useful results. Declining quality may indicate changes in query patterns or data quality.",
        type: "chart",
      },
    ],
  },
  {
    id: "platform-health",
    title: "Platform and compliance",
    icon: <PiShieldCheckLight className="w-4 h-4" />,
    description: "Monitor platform health, user adoption, deadlines, and license usage.",
    metrics: [
      {
        name: "User adoption curve",
        explanation: "A line chart tracking new signups alongside active users over time.",
        howCalculated: "Shows two lines: (1) new user registrations per period, and (2) users who logged in during that same period.",
        whyItMatters: "Healthy adoption means new users are joining and existing users keep coming back. A gap between signups and active users may signal onboarding issues.",
        type: "chart",
      },
      {
        name: "Session devices",
        explanation: "A doughnut chart showing what devices your team uses - Desktop, Mobile, Tablet, etc.",
        howCalculated: "Looks at all user sessions in the period and groups them by reported device type.",
        whyItMatters: "Helps you understand how your team accesses the platform and whether mobile optimization matters.",
        type: "chart",
      },
      {
        name: "Error severity",
        explanation: "A doughnut chart breaking down platform errors by severity - Critical, High, Medium, Low.",
        howCalculated: "Counts reported errors and groups them by severity level.",
        whyItMatters: "A high proportion of critical errors needs immediate attention. Tracking this helps prioritize bug fixes and platform stability.",
        type: "chart",
      },
      {
        name: "Task on-time trend",
        explanation: "A line chart showing what percentage of tasks were completed before their deadline over time.",
        howCalculated: "For each period, checks how many completed tasks met their due date versus how many didn't, then plots the on-time percentage.",
        whyItMatters: "Reveals whether your team consistently meets deadlines or if there's a pattern of delays that needs addressing.",
        type: "chart",
      },
      {
        name: "Knowledge base freshness",
        explanation: "A gauge showing what percentage of your data sources have been updated recently.",
        howCalculated: "Checks which data sources were indexed within the last 30 days. Sources older than 30 days are considered 'stale'.",
        whyItMatters: "Stale knowledge bases may produce outdated research results. Keeping data fresh ensures better AI accuracy.",
        type: "gauge",
      },
      {
        name: "Stale data sources",
        explanation: "The number of data sources that haven't been updated in over 30 days.",
        howCalculated: "Counts data sources where the last indexing date is more than 30 days ago (or never indexed).",
        whyItMatters: "These specific sources should be reviewed and re-indexed to maintain research quality.",
        type: "card",
      },
      {
        name: "License utilization",
        explanation: "A gauge showing what percentage of your purchased license seats are being used.",
        howCalculated: "Divides the number of assigned seats by the total seats in your license plan.",
        whyItMatters: "Low utilization means you're paying for unused seats. High utilization means you may need to plan for expansion.",
        type: "gauge",
      },
      {
        name: "Seats used",
        explanation: "Shows exactly how many license seats are assigned out of your total.",
        howCalculated: "Counts active seat assignments and displays them as 'X / Y' where X is used and Y is total.",
        whyItMatters: "A simple way to check if you need more (or fewer) seats in your plan.",
        type: "card",
      },
    ],
  },
];

const ORG_EXTRA_SECTIONS: HelpSection[] = [
  {
    id: "people",
    title: "People and users",
    icon: <PiUsersLight className="w-4 h-4" />,
    description: "Your team members - who's active, how they're engaging, and session activity.",
    metrics: [
      {
        name: "Team members",
        explanation: "The total number of people in your organization.",
        howCalculated: "Counts all users associated with your organization.",
        whyItMatters: "A baseline measure of your team size.",
        type: "card",
      },
      {
        name: "Active members",
        explanation: "How many team members are currently active.",
        howCalculated: "Counts users who are marked as active and haven't been deactivated.",
        whyItMatters: "Shows your actual working headcount.",
        type: "card",
      },
      {
        name: "Active sessions (24h)",
        explanation: "How many unique user sessions are active right now (within the last 24 hours).",
        howCalculated: "Counts sessions with activity in the last 24 hours.",
        whyItMatters: "A real-time indicator of how many people are using the platform.",
        type: "card",
      },
    ],
  },
  {
    id: "chat",
    title: "Chat and messaging",
    icon: <PiChatsCircleLight className="w-4 h-4" />,
    description: "Organization-wide chat and messaging activity.",
    metrics: [
      {
        name: "Chats",
        explanation: "The total number of chat conversations in your organization.",
        howCalculated: "Counts all active chat channels and direct messages.",
        whyItMatters: "Shows how much communication is happening on the platform.",
        type: "card",
      },
      {
        name: "Messages",
        explanation: "The total number of messages sent across all chats.",
        howCalculated: "Counts every message sent during the selected time period.",
        whyItMatters: "A direct measure of team communication volume.",
        type: "card",
      },
      {
        name: "Messages over time",
        explanation: "A timeline of messaging activity.",
        howCalculated: "Groups messages by date to show communication patterns.",
        whyItMatters: "Reveals peak communication times and long-term trends.",
        type: "chart",
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Collapsible Section Component                                      */
/* ------------------------------------------------------------------ */

const CollapsibleSection: React.FC<{
  section: HelpSection;
  defaultOpen?: boolean;
}> = ({ section, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
      >
        <span className="text-gray-700 dark:text-gray-300">{section.icon}</span>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{section.title}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{section.description}</p>
        </div>
        {isOpen ? (
          <PiCaretDownLight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <PiCaretRightLight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-2 space-y-5 border-t border-gray-100 dark:border-gray-800">
          {section.metrics.map((metric, idx) => (
            <div key={idx} className="pl-4 border-l-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-2">
                <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">{metric.name}</h5>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  {metric.type === "card" ? "Number" : metric.type === "gauge" ? "Gauge" : "Chart"}
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{metric.explanation}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-[#f5f5f5] dark:bg-gray-800/60 rounded-lg px-4 py-3">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">How it's calculated</span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{metric.howCalculated}</p>
                </div>
                <div className="bg-[#f5f5f5] dark:bg-gray-800/60 rounded-lg px-4 py-3">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Why it matters</span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{metric.whyItMatters}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Suggestions Panel                                                  */
/* ------------------------------------------------------------------ */

const SuggestionsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [suggestion, setSuggestion] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!suggestion.trim()) return;
    setSubmitting(true);
    try {
      await apis.post("/analytics/suggestion", { suggestion: suggestion.trim() });
      setSubmitted(true);
      setSuggestion("");
    } catch {
      // Still show success to the user
      setSubmitted(true);
      setSuggestion("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-8 py-6">
      <div className="max-w-xl mx-auto space-y-6">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">{t("Suggest a new analytic")}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            {t("Is there something you'd like to see on your analytics dashboard that isn't there yet? Let us know and we'll consider adding it.")}
          </p>
        </div>

        {submitted ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center space-y-3">
            <PiChatDotsLight className="w-8 h-8 text-gray-400 mx-auto" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t("Thank you for your feedback")}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("We review every suggestion and will consider it for upcoming updates.")}</p>
            </div>
            <button
              onClick={() => setSubmitted(false)}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline transition-colors"
            >
              {t("Submit another suggestion")}
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">{t("What would you like to track?")}</label>
              <textarea
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                placeholder={t("For example: I'd like to see how many matters each team member closes per month, or a chart comparing response times across different practice areas...")}
                className="w-full h-32 px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-[#f5f5f5] dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 placeholder:text-[11px] focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-600 resize-none"
              />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-gray-400">{t("Your suggestion helps us improve the platform for everyone")}</p>
              <Button
                variant="primary"
                size="small"
                onClick={handleSubmit}
                disabled={!suggestion.trim() || submitting}
                isLoading={submitting}
                icon={<PiPaperPlaneRightLight className="w-3.5 h-3.5" />}
              >
                {submitting ? t("Sending...") : t("Submit")}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">{t("Examples of things you can ask for")}</h4>
          {[
            "A comparison of case outcomes across different practice areas",
            "Revenue trend by client over the last 12 months",
            "Average time to first response on new matters",
            "Team utilization rate based on hours logged vs capacity",
          ].map((example, i) => (
            <button
              key={i}
              onClick={() => setSuggestion(example)}
              className="w-full text-left px-4 py-3 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Modal                                                         */
/* ------------------------------------------------------------------ */

type ModalTab = "personal" | "organization" | "suggestions";

const AnalyticsHelpModal: React.FC<AnalyticsHelpModalProps> = ({ isOpen, onClose, isOrgAdmin }) => {
  const { t } = useTranslation();
  const [activeHelp, setActiveHelp] = useState<ModalTab>("personal");

  if (!isOpen) return null;

  const personalSections = PERSONAL_SECTIONS;
  const orgSections = [...PERSONAL_SECTIONS, ...ORG_EXTRA_SECTIONS, ...EXECUTIVE_SECTIONS];

  const sections = activeHelp === "personal" ? personalSections : activeHelp === "organization" ? orgSections : [];

  const totalMetrics = sections.reduce((sum, s) => sum + s.metrics.length, 0);

  const leftTabs: { value: ModalTab; label: string; icon: React.ReactNode }[] = [
    { value: "personal", label: "Personal", icon: <PiUserLight className="w-3.5 h-3.5" /> },
    ...(isOrgAdmin ? [{ value: "organization" as ModalTab, label: "Organization", icon: <PiBuildingsLight className="w-3.5 h-3.5" /> }] : []),
  ];

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[90vh] bg-[#f5f5f5] dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <PiQuestionLight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t("Understanding your analytics")}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {activeHelp === "suggestions"
                  ? t("Tell us what analytics you'd like to see")
                  : `${t("Learn what every metric means and how it's calculated")} - ${totalMetrics} ${t("metrics")}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <PiXLight className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center justify-between px-8 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2">
            {leftTabs.map((tab) => {
              const isActive = activeHelp === tab.value;
              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveHelp(tab.value)}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded transition-all duration-200 border border-1 hover:border-gray-300 hover:bg-white dark:hover:bg-black ${
                    isActive
                      ? "text-primary-600 dark:text-primary-300 bg-white dark:bg-black border-gray-300"
                      : "text-gray-600 dark:text-gray-300 border-transparent"
                  }`}
                >
                  {tab.icon}
                  {t(tab.label)}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveHelp("suggestions")}
              className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded transition-all duration-200 border border-1 hover:border-gray-300 hover:bg-white dark:hover:bg-black ${
                activeHelp === "suggestions"
                  ? "text-primary-600 dark:text-primary-300 bg-white dark:bg-black border-gray-300"
                  : "text-gray-600 dark:text-gray-300 border-transparent"
              }`}
            >
              <PiChatDotsLight className="w-3.5 h-3.5" />
              {t("Suggestions?")}
            </button>
          </div>
        </div>

        {/* Content */}
        {activeHelp === "suggestions" ? (
          <SuggestionsPanel />
        ) : (
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-3">
            {/* Legend */}
            <div className="flex items-center gap-4 mb-2">
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("Numbers")}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("Charts")}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("Gauges")}</span>
              </div>
            </div>

            {sections.map((section) => (
              <CollapsibleSection key={section.id} section={section} />
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <p className="text-[10px] text-gray-400 dark:text-gray-500">
            {t("All data is calculated from the time range you've selected. Adjusting the date filter will update every metric.")}
          </p>
          <Button variant="neutral" size="small" onClick={onClose}>
            {t("Close")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsHelpModal;
